import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser } from '../../store/slices/authSlice';
import api from '../../services/api';
import './ForceChangePassword.css';

/**
 * ForceChangePassword — shown to local users on first login (must_change_password=true).
 * Calls POST /auth/change-password, then clears the flag in Redux and navigates to dashboard.
 */
const ForceChangePassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const checkRequirements = (pwd) => ({
    length: pwd.length >= 12,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    digit: /\d/.test(pwd),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
  });

  const reqs = checkRequirements(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!currentPassword) {
      errors.current = 'Current password is required.';
    }

    const missing = [];
    if (!reqs.length) missing.push('at least 12 characters');
    if (!reqs.upper) missing.push('an uppercase letter');
    if (!reqs.lower) missing.push('a lowercase letter');
    if (!reqs.digit) missing.push('a digit');
    if (!reqs.special) missing.push('a special character');
    if (missing.length) {
      errors.new = `Password must contain ${missing.join(', ')}.`;
    }

    if (!confirmPassword) {
      errors.confirm = 'Please confirm your new password.';
    } else if (newPassword !== confirmPassword) {
      errors.confirm = 'Passwords do not match.';
    }

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setSubmitError('');
    setSubmitting(true);

    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      // Clear must_change_password in Redux + localStorage
      const updatedUser = { ...user, must_change_password: false };
      dispatch(updateUser(updatedUser));

      navigate('/dashboard', { replace: true });
    } catch (err) {
      setSubmitError(
        err.response?.data?.detail || 'Failed to change password. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-form">
      <h2>Change Your Password</h2>
      <p className="fcp-subtitle">
        You must set a new password before continuing. This is required on first login.
      </p>

      {submitError && (
        <div className="form-error login-error-banner">{submitError}</div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label className="form-label" htmlFor="fcp-current">Current Password</label>
          <input
            id="fcp-current"
            type="password"
            className={`form-input${fieldErrors.current ? ' error' : ''}`}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter your current password"
            autoComplete="current-password"
            disabled={submitting}
          />
          {fieldErrors.current && (
            <span className="field-error">{fieldErrors.current}</span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="fcp-new">New Password</label>
          <input
            id="fcp-new"
            type="password"
            className={`form-input${fieldErrors.new ? ' error' : ''}`}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Create a new password"
            autoComplete="new-password"
            disabled={submitting}
          />
          {fieldErrors.new && (
            <span className="field-error">{fieldErrors.new}</span>
          )}
        </div>

        {newPassword.length > 0 && (
          <div className="password-requirements">
            <p className="requirements-title">Requirements</p>
            <ul>
              <li className={reqs.length ? 'met' : ''}>At least 12 characters</li>
              <li className={reqs.upper ? 'met' : ''}>At least one uppercase letter</li>
              <li className={reqs.lower ? 'met' : ''}>At least one lowercase letter</li>
              <li className={reqs.digit ? 'met' : ''}>At least one digit</li>
              <li className={reqs.special ? 'met' : ''}>At least one special character</li>
            </ul>
          </div>
        )}

        <div className="form-group">
          <label className="form-label" htmlFor="fcp-confirm">Confirm New Password</label>
          <input
            id="fcp-confirm"
            type="password"
            className={`form-input${fieldErrors.confirm ? ' error' : ''}`}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your new password"
            autoComplete="new-password"
            disabled={submitting}
          />
          {fieldErrors.confirm && (
            <span className="field-error">{fieldErrors.confirm}</span>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          style={{ width: '100%', justifyContent: 'center', marginTop: 'var(--space-2)' }}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <span className="spinner" style={{ borderTopColor: '#fff', borderColor: 'rgba(255,255,255,0.3)', width: '16px', height: '16px', marginRight: '8px' }} />
              Changing password...
            </>
          ) : (
            'Set New Password'
          )}
        </button>
      </form>
    </div>
  );
};

export default ForceChangePassword;
